import type { SiteEntity } from '../types/site';
import type { SiteBoundaryParcel } from '../types/siteBoundary';
import type { SiteStudyArea, SiteStudyAreaScope, StudyGeometryRef, StudyGeometryRole } from '../types/siteStudyArea';
import { computeAggregate, makeParcel, materializeSiteBoundaries, polygonArea, polygonPerimeter } from './siteBoundaryGeometry';
import { createRawParcelBoundarySnapshot, sha256HexForGenerationImpactValue } from './generationImpact/boundaryAuthority';
import { detectStrayVertex, type StrayVertexRepair } from './polygonSpurRepair';

export type StudyAreaSite = Pick<SiteEntity, 'siteBoundaries' | 'siteBoundary' | 'siteArea' | 'sitePerimeter' | 'siteCentroid' | 'siteBoundaryAggregate' | 'siteLayers' | 'studyArea' | 'studyAreaScope'>;
export interface StudyAreaCandidate extends StudyGeometryRef {
  name: string;
  geometryType: 'polygon' | 'line' | 'point';
  polygon: Array<{ lat: number; lng: number }>;
  holes?: Array<Array<{ lat: number; lng: number }>>;
  geometryHash?: string;
  areaAcres?: number;
  error?: string;
  /**
   * Present only when `error` is set AND a single stray vertex explains it.
   * The UI offers this as a repair rather than applying it: editing a study
   * polygon invalidates downstream reports, so it is the operator's call.
   */
  repair?: StrayVertexRepair;
}
export const STUDY_GEOMETRY_ROLES: ReadonlyArray<{ value: StudyGeometryRole; label: string }> = [
  { value: 'development', label: 'Development area' },
  { value: 'property', label: 'Property / study extent' },
  { value: 'corridor', label: 'Infrastructure / easement corridor' },
  { value: 'reference', label: 'Reference' },
];
export const studyGeometryKey = (ref: StudyGeometryRef): string => `${ref.kind}:${ref.id}`;

const hashPrefix = (hash: string | null | undefined): string => hash ? `${hash.slice(0, 8)}…` : 'missing';

/**
 * Names both sides of a pin mismatch and the one action that fixes it.
 *
 * A pin is the hash of the outline the operator reviewed; the saved outline is
 * what the document holds now. They differ either because the outline was
 * edited after the review (re-review is required) or because the pin was
 * computed over an outline that was never persisted (Pineywoods p2, 2026-09-15).
 * Either way the fix is the same, and the hashes let an operator confirm which
 * case they are in against the document.
 */
export function studyPolygonChangedMessage(name: string, pinnedHash: string, savedHash: string): string {
  return `Study polygon ${name} changed: the study area pins outline ${hashPrefix(pinnedHash)} but the saved outline is ${hashPrefix(savedHash)}. Open Study area, review the selection and save it again so the pin matches the saved outline before continuing.`;
}

type StudyPolygon = StudyAreaCandidate['polygon'];
type StudyPolygonEvaluation = { geometryHash: string; areaAcres: number } | { error: string; repair?: StrayVertexRepair };
/**
 * Validation is a pure function of the polygon's points, and the polygon array itself is the key:
 * updateSiteLayers spreads layers but keeps each geometry reference, so an unchanged layer is never
 * re-validated (Goldenrod: 261 regional layers, ~86k vertices, re-validated on every store update).
 * A geometry edit replaces the array. The repair proposal is computed only when a caller asks for it.
 */
const studyPolygonMemo = new WeakMap<StudyPolygon, { geometryHash: string; areaAcres: number } | { error: string; repair?: StrayVertexRepair | null }>();

export function evaluateStudyPolygon(
  polygon: StudyPolygon,
  options: { withRepair?: boolean; holes?: Array<Array<{ lat: number; lng: number }>> } = {},
): StudyPolygonEvaluation {
  const hasHoles = Boolean(options.holes && options.holes.length > 0);
  let entry = !hasHoles ? studyPolygonMemo.get(polygon) : undefined;
  if (!entry) {
    try {
      const boundary = hasHoles
        ? createRawParcelBoundarySnapshot({
            siteBoundaries: [makeParcel(polygon, { id: 'eval', holes: options.holes })],
          })
        : createRawParcelBoundarySnapshot({ siteBoundary: polygon });
      entry = { geometryHash: boundary.boundaryHash, areaAcres: boundary.areaAcres };
    } catch (error) {
      // Keep the real message. A bare "Invalid geometry" tells an operator that
      // their parcel is unusable without telling them why or what to do.
      entry = { error: (error as Error).message };
    }
    if (!hasHoles) studyPolygonMemo.set(polygon, entry);
  }
  if (!('error' in entry)) return entry;
  if (options.withRepair && entry.repair === undefined) {
    try { entry.repair = detectStrayVertex(polygon); } catch { entry.repair = null; }
  }
  return { error: entry.error, ...(options.withRepair && entry.repair ? { repair: entry.repair } : {}) };
}

/** Every polygon, parcel and layer, in registry order; nothing is validated here. */
function studyAreaCandidateRows(site: StudyAreaSite): StudyAreaCandidate[] {
  // An explicitly empty registry is a deletion state, not permission to revive its legacy alias.
  const parcels = Array.isArray(site.siteBoundaries) && site.siteBoundaries.length === 0 ? [] : materializeSiteBoundaries(site);
  return [
    ...parcels.map(parcel => ({
      kind: 'parcel' as const,
      id: parcel.id,
      name: parcel.label ?? parcel.id,
      geometryType: 'polygon' as const,
      polygon: parcel.polygon,
      ...(parcel.holes && parcel.holes.length > 0 ? { holes: parcel.holes } : {}),
    })),
    ...(site.siteLayers ?? []).map(layer => ({
      kind: 'layer' as const,
      id: layer.id,
      name: layer.name,
      geometryType: layer.geometry.type,
      polygon: layer.geometry.coordinates,
      ...(layer.geometry.holes && layer.geometry.holes.length > 0 ? { holes: layer.geometry.holes } : {}),
    })),
  ];
}

function evaluatedCandidate(candidate: StudyAreaCandidate, withRepair: boolean): StudyAreaCandidate {
  if (candidate.geometryType !== 'polygon') return candidate;
  const evaluation = evaluateStudyPolygon(candidate.polygon, { withRepair, holes: candidate.holes });
  if ('error' in evaluation) return { ...candidate, error: evaluation.error, repair: evaluation.repair };
  return { ...candidate, geometryHash: evaluation.geometryHash, areaAcres: evaluation.areaAcres };
}

/** Polygon hashes use the existing validated, rotation/winding-independent authority. */
export function listStudyAreaCandidates(site: StudyAreaSite): StudyAreaCandidate[] {
  return studyAreaCandidateRows(site).map(candidate => evaluatedCandidate(candidate, true));
}

/** An explicit invalid selection always fails closed; legacy sites retain their old scope. */
export function resolveSiteStudyArea(site: StudyAreaSite): null | {
  scope: SiteStudyAreaScope;
  parcels: SiteBoundaryParcel[];
  boundary: ReturnType<typeof createRawParcelBoundarySnapshot>;
} {
  const selection = site.studyArea;
  if (!selection) return null;
  if (selection.schemaVersion !== 1 || !Number.isInteger(selection.revision) || selection.revision < 1
    || !selection.name?.trim() || !Array.isArray(selection.included) || !selection.included.length
    || selection.included.length > 100 || !Array.isArray(selection.roles)) {
    throw new Error('Choose at least one valid polygon in Study area before running the study.');
  }
  // Only the included polygons are validated; the rest of the registry is context (and may be huge).
  const byKey = new Map<string, StudyAreaCandidate>();
  for (const row of studyAreaCandidateRows(site)) if (!byKey.has(studyGeometryKey(row))) byKey.set(studyGeometryKey(row), row);
  const seen = new Set<string>();
  const sources: SiteStudyAreaScope['sources'] = [];
  const parcels = selection.included.map(ref => {
    const key = studyGeometryKey(ref);
    if (seen.has(key)) throw new Error(`Study area contains a duplicate polygon: ${key}.`);
    seen.add(key);
    const row = byKey.get(key);
    const candidate = row && evaluatedCandidate(row, false);
    if (!candidate || candidate.geometryType !== 'polygon' || !candidate.geometryHash) {
      throw new Error(`Study polygon ${candidate?.name ?? ref.id} is missing or invalid. Review Study area before continuing.`);
    }
    if (candidate.geometryHash !== ref.geometryHash) {
      throw new Error(studyPolygonChangedMessage(candidate.name, ref.geometryHash, candidate.geometryHash));
    }
    const role = selection.roles.find(row => studyGeometryKey(row) === key)?.role;
    if (role !== 'development' && role !== 'property') {
      throw new Error(`Study polygon ${candidate.name} must have a development or property role.`);
    }
    sources.push({ kind: ref.kind, id: ref.id, name: candidate.name, geometryHash: candidate.geometryHash });
    return makeParcel(candidate.polygon, {
      id: ref.kind === 'parcel' ? ref.id : `study-layer:${ref.id}`,
      label: candidate.name,
      ...(candidate.holes && candidate.holes.length > 0 ? { holes: candidate.holes } : {}),
    });
  });
  const boundary = createRawParcelBoundarySnapshot({ siteBoundaries: parcels });
  const selectionHash = sha256HexForGenerationImpactValue(JSON.stringify({
    schemaVersion: 1, name: selection.name.trim(), revision: selection.revision,
    // Firestore map field order is not authority. Preserve the original typed
    // projection's bytes while retaining every geometry, role and revision pin.
    included: selection.included.map(ref => ({ kind: ref.kind, id: ref.id, geometryHash: ref.geometryHash }))
      .sort((a, b) => studyGeometryKey(a).localeCompare(studyGeometryKey(b))),
    roles: selection.roles.map(ref => ({ kind: ref.kind, id: ref.id, role: ref.role }))
      .sort((a, b) => studyGeometryKey(a).localeCompare(studyGeometryKey(b))),
    geometryHash: boundary.boundaryHash, areaMethodVersion: 'spherical-mean-radius-6371000-v1',
  }));
  return {
    parcels, boundary,
    scope: {
      schemaVersion: 1, name: selection.name.trim(), revision: selection.revision, selectionHash,
      geometryHash: boundary.boundaryHash, areaMethodVersion: 'spherical-mean-radius-6371000-v1',
      grossAreaAcres: boundary.areaAcres, polygonCount: boundary.polygons.length,
      hasHoles: boundary.polygons.some(polygon => polygon.length > 1), sources,
    },
  };
}

/** Derive query centre and perimeter from the union, so overlapping sources have no extra weight. */
function unionMetrics(polygons: ReturnType<typeof createRawParcelBoundarySnapshot>['polygons']) {
  let area = 0, lat = 0, lng = 0, perimeter = 0;
  for (const polygon of polygons) for (const [ringIndex, ring] of polygon.entries()) {
    const origin = ring[0]!;
    let crossSum = 0, cx = 0, cy = 0;
    for (let index = 0; index < ring.length; index++) {
      const a = ring[index]!, b = ring[(index + 1) % ring.length]!;
      const ax = a.lng - origin.lng, ay = a.lat - origin.lat;
      const bx = b.lng - origin.lng, by = b.lat - origin.lat;
      const cross = ax * by - bx * ay;
      crossSum += cross; cx += (ax + bx) * cross; cy += (ay + by) * cross;
    }
    if (Math.abs(crossSum) < Number.EPSILON) throw new Error('Study area contains a degenerate union ring.');
    const weight = polygonArea(ring) * (ringIndex === 0 ? 1 : -1);
    area += weight;
    lat += (origin.lat + cy / (3 * crossSum)) * weight;
    lng += (origin.lng + cx / (3 * crossSum)) * weight;
    perimeter += polygonPerimeter(ring);
  }
  return { centroid: { lat: lat / area, lng: lng / area }, perimeter };
}

/** Detached calculation view. Never persist these aliases over the source registry. */
export function applySiteStudyArea<T extends StudyAreaSite>(site: T): T & StudyAreaSite {
  const resolved = resolveSiteStudyArea(site);
  if (!resolved) return site;
  const aggregate = computeAggregate(resolved.parcels);
  const metrics = unionMetrics(resolved.boundary.polygons);
  return {
    ...site, siteBoundaries: resolved.parcels,
    // A single-ring consumer must never receive the first part of a multi-polygon.
    siteBoundary: resolved.boundary.polygons.length === 1 && resolved.boundary.polygons[0]!.length === 1
      ? resolved.boundary.polygons[0]![0] : undefined,
    siteBoundaryAggregate: { ...aggregate, totalArea: resolved.boundary.areaAcres, totalPerimeter: metrics.perimeter, aggregateCentroid: metrics.centroid, isDisjoint: resolved.boundary.polygons.length > 1 },
    siteArea: resolved.boundary.areaAcres, sitePerimeter: metrics.perimeter,
    siteCentroid: metrics.centroid, studyAreaScope: resolved.scope,
  };
}

export function createSiteStudyArea(
  site: StudyAreaSite,
  input: { name: string; includedKeys: string[]; roles: Record<string, StudyGeometryRole>; updatedAt: string },
): SiteStudyArea {
  const candidates = listStudyAreaCandidates(site);
  const selection: SiteStudyArea = {
    schemaVersion: 1, name: input.name.trim(), revision: (site.studyArea?.revision ?? 0) + 1,
    updatedAt: input.updatedAt,
    included: input.includedKeys.map(key => {
      const candidate = candidates.find(row => studyGeometryKey(row) === key);
      if (!candidate?.geometryHash) throw new Error('Select valid polygons only.');
      return { kind: candidate.kind, id: candidate.id, geometryHash: candidate.geometryHash };
    }),
    roles: candidates.map(candidate => ({ kind: candidate.kind, id: candidate.id, role: input.roles[studyGeometryKey(candidate)] ?? 'reference' })),
  };
  resolveSiteStudyArea({ ...site, studyArea: selection });
  return selection;
}

/**
 * Pins bind to persisted geometry, so a selection is only saveable when every
 * pin reproduces from the document itself.
 *
 * `studyArea` was computed from whatever the editor was showing (the store's
 * copy, or the boundary editor's local copy); `persisted` is the document read
 * back from Firestore. The store cannot stand in for the document: updateSite
 * re-applies the caller's patch over the refreshed document, so a ring that was
 * never written survives in memory and every in-memory re-validation passes.
 *
 * Fails closed. A mismatch is never resolved by silently re-pinning the saved
 * outline, because the operator reviewed the outline on screen, not that one.
 */
export function assertStudyAreaPinsMatchPersisted(studyArea: SiteStudyArea, persisted: StudyAreaSite): void {
  if ((persisted.studyArea?.revision ?? 0) + 1 !== studyArea.revision) {
    throw new Error('The study selection was saved again while this editor was open. Reload the site and review the latest selection.');
  }
  const candidates = listStudyAreaCandidates(persisted);
  const mismatches = studyArea.included.flatMap(ref => {
    const candidate = candidates.find(row => studyGeometryKey(row) === studyGeometryKey(ref));
    if (candidate?.geometryHash === ref.geometryHash) return [];
    return [`The outline for ${candidate?.name ?? ref.id} shown here (${hashPrefix(ref.geometryHash)}) is not the outline saved for this site (${hashPrefix(candidate?.geometryHash)}).`];
  });
  if (mismatches.length > 0) {
    throw new Error(`${mismatches.join(' ')} Reload the site, then review and save the study area again.`);
  }
  // Roles, duplicates and validity are judged over the document too.
  resolveSiteStudyArea({ ...persisted, studyArea });
}

/** Stable input receipt also exposes broken source references to currency checks. */
export function siteStudyAreaInputIdentity(site: StudyAreaSite): unknown {
  if (!site.studyArea) return null;
  try { return resolveSiteStudyArea(site)!.scope; }
  catch (error) { return { selection: site.studyArea, invalid: (error as Error).message }; }
}

export function studyAreaPromptBlock(scope: SiteStudyAreaScope | undefined): string {
  if (!scope) return '';
  return `DEVELOPMENT STUDY AREA: ${scope.name}. Selected polygons: ${scope.sources.map(source => source.name).join('; ')}. Gross selected area: ${scope.grossAreaAcres.toFixed(2)} acres (${scope.areaMethodVersion}). Scope hash: ${scope.selectionHash}. Use this footprint for land, layout, capacity and financial quantities. Other imported polygons are context. Gross area is not usable/buildable acreage; do not subtract survey, wetland or easement quantities unless their coverage matches this footprint. Missing spatial coverage remains unknown.`;
}
